import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { orderAPI } from '../../api'
import type { OrderFormData, OrderStatus } from '../../types'

// Query key factory for orders
export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (filters: OrderFilters) => [...orderKeys.lists(), filters] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: number) => [...orderKeys.details(), id] as const,
  customerOrders: (customerId: number) => [...orderKeys.all, 'customer', customerId] as const,
}

export interface OrderFilters {
  page?: number
  perPage?: number
  search?: string
  status?: string
  customerId?: number
}

// Fetch orders list with filters
export function useOrders(filters: OrderFilters = {}) {
  const {
    page = 1,
    perPage = 20,
    search = '',
    status = '',
    customerId,
  } = filters

  return useQuery({
    queryKey: orderKeys.list({ page, perPage, search, status, customerId }),
    queryFn: () => orderAPI.getOrders(page, perPage, search, status, customerId),
  })
}

// Fetch single order
export function useOrder(orderId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: () => orderAPI.getOrder(orderId),
    enabled: options?.enabled ?? orderId > 0,
  })
}

// Fetch orders for a specific customer
export function useCustomerOrders(customerId: number | null, page = 1, perPage = 20) {
  return useQuery({
    queryKey: orderKeys.customerOrders(customerId!),
    queryFn: () => orderAPI.getCustomerOrders(customerId!, page, perPage),
    enabled: customerId !== null,
  })
}

// Fetch authenticated customer's own orders (customer-facing endpoint)
export function useMyOrders(page = 1, perPage = 20, search = '') {
  return useQuery({
    queryKey: [...orderKeys.all, 'my-orders', page, perPage, search] as const,
    queryFn: () => orderAPI.getMyOrders(page, perPage, search),
  })
}

// Create order mutation
export function useCreateOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (orderData: OrderFormData) => orderAPI.createOrder(orderData),
    onSuccess: (data) => {
      // Invalidate all order lists
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      // Also invalidate customer orders if applicable
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}

// Update order mutation
export function useUpdateOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<OrderFormData> }) =>
      orderAPI.updateOrder(id, data),
    onSuccess: (data) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderKeys.detail(data.order.id), { order: data.order })
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      // Also invalidate customer orders
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}

export function useBulkUpdateOrderStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: string }) =>
      orderAPI.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
    },
  })
}

// Update order status mutation
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status, tracking }: { id: number; status: OrderStatus; tracking?: { tracking_number: string; carrier: string } }) =>
      orderAPI.updateOrderStatus(id, status, tracking),
    onSuccess: (data) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderKeys.detail(data.order.id), { order: data.order })
      // Invalidate lists to reflect status change
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      // Also invalidate customer orders
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}

// Fulfill order (admin confirms available quantities)
export function useFulfillOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, items }: { id: number; items: Array<{ order_item_id: number; fulfilled_quantity: number }> }) =>
      orderAPI.fulfillOrder(id, items),
    onSuccess: (data) => {
      queryClient.setQueryData(orderKeys.detail(data.order.id), { order: data.order })
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}

// Cancel a child backorder (admin)
export function useCancelBackorder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderId, backorderId }: { orderId: number; backorderId: number }) =>
      orderAPI.cancelBackorder(orderId, backorderId),
    onSuccess: (data) => {
      queryClient.setQueryData(orderKeys.detail(data.order.id), { order: data.order })
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}

// Cancel order (customer-initiated)
export function useCancelOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (orderId: number) =>
      orderAPI.cancelOrder(orderId),
    onSuccess: (data) => {
      // Update the specific order in cache
      queryClient.setQueryData(orderKeys.detail(data.order.id), { order: data.order })
      // Invalidate customer orders list
      if (data.order.customer_id) {
        queryClient.invalidateQueries({ queryKey: orderKeys.customerOrders(data.order.customer_id) })
      }
    },
  })
}
